import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { CalendarDays, MapPin, Users, Minus, Plus, ArrowLeft, Building2, Loader2, ExternalLink, X, BadgePercent, Share2, Facebook, Mail, Link2, Smartphone, HandCoins, CircleHelp, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { PublicLayout } from "../components/PublicLayout";
import { OrganizerContactBlock } from "../components/OrganizerContactBlock";
import { ShopTeaser } from "../components/shop/ShopTeaser";
import { BasarTeaser } from "../components/basar/BasarTeaser";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { api, formatDateTime, errorMessage, FALLBACK_IMAGE, imageSrc, priceLabel, eventShareUrl, openFacebookShare } from "../lib/api";
import { categoryStyle } from "../lib/categories";
import { useLang } from "../context/LanguageContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

export default function EventDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLang();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [isOver, setIsOver] = useState(false); // 410: arrangementsdatoen er forbi – berre arrangøren ser sida
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [typeQty, setTypeQty] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [discountCodeInput, setDiscountCodeInput] = useState("");
  const [discount, setDiscount] = useState(null);
  const [checkingDiscount, setCheckingDiscount] = useState(false);
  const [wlName, setWlName] = useState("");
  const [wlEmail, setWlEmail] = useState("");
  const [wlSubmitting, setWlSubmitting] = useState(false);
  const [wlJoined, setWlJoined] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("vipps"); // "vipps" | "door" («Reserver billetter – betal ved inngangen»)
  useEffect(() => {
    // Når Vipps ikke er klart men reservasjon er åpnet, starter vi på «Reserver billetter» så salget er åpent med en gang
    if (event && !event.payments_enabled && event.pay_at_door) setPaymentMethod("door");
  }, [event?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api
      .get(`/events/${id}`)
      .then((res) => {
        setEvent(res.data);
        const types = res.data.ticket_types || [];
        if (types.length > 0) {
          const init = {};
          types.forEach((t, i) => { init[t] = i === 0 ? 1 : 0; });
          setTypeQty(init);
        }
      })
      .catch((err) => (err?.response?.status === 410 ? setIsOver(true) : setNotFound(true)))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    const key = `ri-viewed-${id}`;
    if (!sessionStorage.getItem(key)) {
      sessionStorage.setItem(key, "1");
      api.post(`/events/${id}/view`).catch(() => {});
    }
  }, [id]);

  const hasTypes = event?.ticket_types?.length > 0;
  const isExternal = event?.ticketing === "external"; // billetter/påmelding hos arrangøren (Facebook e.l.)
  const externalIsFacebook = event?.external_host === "Facebook";
  const soldOut = event && !isExternal && event.remaining <= 0;
  const maxQty = event ? Math.min(10, Math.max(1, event.remaining)) : 10;
  const totalQty = hasTypes ? Object.values(typeQty).reduce((a, b) => a + b, 0) : quantity;

  const unitPrice = (t) => {
    if (t && event?.type_prices && event.type_prices[t] != null) return event.type_prices[t];
    return event?.price || 0;
  };
  const totalAmount = hasTypes
    ? Object.entries(typeQty).reduce((sum, [t, q]) => sum + q * unitPrice(t), 0)
    : quantity * unitPrice(null);
  const isPaid = (event?.max_price ?? 0) > 0;
  const cat = categoryStyle(event?.category);
  const CatIcon = cat.Icon;
  const discountAmount = discount
    ? discount.type === "percent"
      ? Math.floor((totalAmount * discount.value) / 100)
      : Math.min(discount.value, totalAmount)
    : 0;
  const payableAmount = totalAmount - discountAmount;
  // «Betal ved inngangen»: arrangøren har åpnet for at publikum kan reservere QR-billett uten å betale med Vipps nå
  const doorAvailable = payableAmount > 0 && !!event?.pay_at_door;
  const vippsAvailable = !!event?.payments_enabled;
  const payAtDoor = doorAvailable && paymentMethod === "door";
  // Arrangøren har åpnet for reservasjon, men Vipps er ikke klart ennå: valget vises, men kan ikke brukes i dag
  const vippsPending = doorAvailable && !vippsAvailable && paymentMethod === "vipps";
  const paymentsBlocked = payableAmount > 0 && !vippsAvailable && !doorAvailable;

  const applyDiscount = async () => {
    const code = discountCodeInput.trim();
    if (!code) return;
    setCheckingDiscount(true);
    try {
      const { data } = await api.post(`/events/${id}/validate-discount`, { code });
      setDiscount(data);
      setDiscountCodeInput("");
      toast.success(t("Rabattkode {code} aktivert – {desc}", { code: data.code, desc: data.description }));
    } catch (err) {
      setDiscount(null);
      toast.error(errorMessage(err, t("Ugyldig rabattkode")));
    } finally {
      setCheckingDiscount(false);
    }
  };

  const removeDiscount = () => setDiscount(null);

  const adjustType = (type, delta) => {
    setTypeQty((prev) => {
      const next = { ...prev };
      const newVal = Math.max(0, (next[type] || 0) + delta);
      const otherTotal = Object.entries(next).reduce((a, [k, v]) => (k === type ? a : a + v), 0);
      next[type] = Math.min(newVal, Math.max(0, maxQty - otherTotal));
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error(t("Fyll inn e-post"));
      return;
    }
    let cleanedPhone = phone.trim().replace(/[\s.\-()]/g, "").replace(/^\+47/, "").replace(/^0047/, "");
    if (phone.trim() && !/^\d{8}$/.test(cleanedPhone)) {
      toast.error(t("Telefonnummer må ha 8 siffer (norsk nummer)"));
      return;
    }
    if (totalQty < 1) {
      toast.error(t("Velg minst én billett"));
      return;
    }
    setSubmitting(true);
    try {
      const payload = { event_id: id, name: name.trim(), email: email.trim(), phone: cleanedPhone };
      if (discount) payload.discount_code = discount.code;
      if (payAtDoor) payload.payment_method = "door";
      if (hasTypes) {
        payload.items = Object.entries(typeQty)
          .filter(([, q]) => q > 0)
          .map(([t, q]) => ({ ticket_type: t, quantity: q }));
      } else {
        payload.quantity = quantity;
      }
      const { data } = await api.post("/orders", payload);
      if (data.payment_redirect_url) {
        toast.info(t("Sender deg til Vipps for betaling..."));
        window.location.href = data.payment_redirect_url;
        return;
      }
      toast.success(payAtDoor ? t("Billettene er reservert – husk å betale ved inngangen") : t("Billettene dine er klare!"));
      navigate(`/billetter/${data.order.id}`);
    } catch (err) {
      toast.error(errorMessage(err));
      api.get(`/events/${id}`).then((res) => setEvent(res.data)).catch(() => {});
    } finally {
      setSubmitting(false);
    }
  };

  const joinWaitlist = async (e) => {
    e.preventDefault();
    if (!wlName.trim() || !wlEmail.trim()) {
      toast.error(t("Fyll inn navn og e-post"));
      return;
    }
    setWlSubmitting(true);
    try {
      await api.post(`/events/${id}/waitlist`, { name: wlName.trim(), email: wlEmail.trim() });
      setWlJoined(true);
      toast.success(t("Du står nå på ventelisten!"));
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setWlSubmitting(false);
    }
  };

  if (loading) {
    return (
      <PublicLayout>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7 space-y-4">
            <Skeleton className="aspect-[16/9] rounded-2xl" />
            <Skeleton className="h-8 w-2/3" />
            <Skeleton className="h-4 w-1/2" />
          </div>
          <div className="lg:col-span-5">
            <Skeleton className="h-96 rounded-2xl" />
          </div>
        </div>
      </PublicLayout>
    );
  }

  if (isOver) {
    return (
      <PublicLayout>
        <div className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="event-over">
          <CalendarDays className="w-10 h-10 mx-auto mb-4 text-muted-foreground" />
          <h1 className="text-2xl font-semibold mb-2">{t("Arrangementet er avsluttet")}</h1>
          <p className="text-muted-foreground mb-6">{t("Datoen for dette arrangementet er passert, og det vises ikke lenger for publikum. Takk til alle som kom!")}</p>
          <Button asChild>
            <Link to="/"><ArrowLeft className="w-4 h-4 mr-1.5" />{t("Se kommende arrangementer")}</Link>
          </Button>
        </div>
      </PublicLayout>
    );
  }

  if (notFound) {
    return (
      <PublicLayout>
        <div className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="event-not-found">
          <h1 className="text-2xl font-semibold mb-2">{t("Fant ikke arrangementet")}</h1>
          <p className="text-muted-foreground mb-6">{t("Arrangementet finnes ikke eller er ikke publisert lenger.")}</p>
          <Button asChild>
            <Link to="/"><ArrowLeft className="w-4 h-4 mr-1.5" />{t("Tilbake til oversikten")}</Link>
          </Button>
        </div>
      </PublicLayout>
    );
  }

  const fullAddress = [event.address, [event.postal_code, event.poststed || event.city].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  const mapQuery = event.lat && event.lon
    ? `${event.lat},${event.lon}`
    : [event.venue, event.address, event.city, "Norge"].filter(Boolean).join(", ");
  const hasLocation = !!(event.lat && event.lon) || !!(event.address || event.city);
  const externalMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    [event.venue, event.address, event.postal_code, event.city].filter(Boolean).join(", ") || mapQuery
  )}`;

  return (
    <PublicLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors duration-150 mb-6" data-testid="event-back-link">
          <ArrowLeft className="w-4 h-4" />
          {t("Alle arrangementer")}
        </Link>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
          {/* Left: info */}
          <div className="lg:col-span-7 space-y-6">
            <div className="rounded-2xl overflow-hidden border border-border bg-muted">
              <img
                src={imageSrc(event.image_url)}
                alt={event.title}
                className="w-full aspect-[16/9] object-cover"
                onError={(e) => { e.currentTarget.src = FALLBACK_IMAGE; }}
              />
            </div>
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2 items-center">
                <Badge
                  variant="outline"
                  className={`gap-1 ${cat.badge}`}
                  data-testid="event-detail-category-badge"
                >
                  <CatIcon className="w-3 h-3" aria-hidden="true" />
                  {t(event.category)}
                </Badge>
                {isExternal ? (
                  <Badge variant="outline" className="gap-1 border-border bg-card" data-testid="event-detail-external-badge">
                    {externalIsFacebook ? <Facebook className="w-3 h-3" aria-hidden="true" /> : <ExternalLink className="w-3 h-3" aria-hidden="true" />}
                    {event.external_price_text || (externalIsFacebook ? t("Facebook-arrangement") : t("Eksternt arrangement"))}
                  </Badge>
                ) : soldOut ? (
                  <Badge className="bg-[hsl(var(--danger))] text-[hsl(var(--danger-foreground))] hover:bg-[hsl(var(--danger))]">{t("Utsolgt")}</Badge>
                ) : (
                  <Badge
                    className={
                      isPaid
                        ? "bg-primary text-primary-foreground hover:bg-primary"
                        : "bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))] hover:bg-[hsl(var(--success))]"
                    }
                    data-testid="event-detail-price-badge"
                  >
                    {priceLabel(event)}
                  </Badge>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="secondary" size="sm" className="ml-auto" data-testid="event-share-button">
                      <Share2 className="w-4 h-4 mr-1.5" />
                      {t("Del")}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuItem onClick={() => openFacebookShare(eventShareUrl(event.id))} data-testid="event-share-facebook">
                      <Facebook className="w-4 h-4 mr-2" /> {t("Del på Facebook")}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        const subject = encodeURIComponent(event.title);
                        const body = encodeURIComponent(t(isExternal ? "Hei!\n\nSjekk ut «{title}» – {date}.\n\nLes mer her: {url}" : "Hei!\n\nSjekk ut «{title}» – {date}.\n\nHent billetter her: {url}", { title: event.title, date: formatDateTime(event.date), url: eventShareUrl(event.id) }));
                        window.location.href = `mailto:?subject=${subject}&body=${body}`;
                      }}
                      data-testid="event-share-email"
                    >
                      <Mail className="w-4 h-4 mr-2" /> {t("Del på e-post")}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(eventShareUrl(event.id));
                          toast.success(t("Delingslenken er kopiert – lim den inn hvor som helst"));
                        } catch {
                          toast.error(t("Kunne ikke kopiere lenken"));
                        }
                      }}
                      data-testid="event-share-copy"
                    >
                      <Link2 className="w-4 h-4 mr-2" /> {t("Kopier delingslenke")}
                    </DropdownMenuItem>
                    {typeof navigator !== "undefined" && navigator.share && (
                      <DropdownMenuItem
                        onClick={() => {
                          navigator
                            .share({ title: event.title, text: `${event.title} – ${formatDateTime(event.date)}`, url: eventShareUrl(event.id) })
                            .catch(() => {});
                        }}
                        data-testid="event-share-native"
                      >
                        <Share2 className="w-4 h-4 mr-2" /> {t("Del via mobil...")}
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              {event.is_over && (
                <div className="rounded-xl border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 px-4 py-3 text-sm" data-testid="event-over-organizer-notice">
                  <p className="font-medium">{t("Arrangementet er avsluttet")}</p>
                  <p className="text-muted-foreground">{t("Datoen er passert – siden vises nå bare for deg som arrangør. Publikum får beskjed om at arrangementet er avsluttet.")}</p>
                </div>
              )}
              <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight" data-testid="event-title">{event.title}</h1>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <p className="flex items-center gap-2 text-muted-foreground">
                  <CalendarDays className="w-4 h-4 shrink-0" />
                  {formatDateTime(event.date)}
                </p>
                <p className="flex items-start gap-2 text-muted-foreground">
                  <MapPin className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>
                    {[event.venue, event.city].filter(Boolean).join(", ") || t("Sted kommer")}
                    {fullAddress && <span className="block text-xs">{fullAddress}</span>}
                  </span>
                </p>
                <p className="flex items-center gap-2 text-muted-foreground">
                  <Building2 className="w-4 h-4 shrink-0" />
                  <span>
                    {t("Arrangør:")}{" "}
                    <Link
                      to={`/arrangor/${event.organizer_id}`}
                      className="font-medium text-foreground underline underline-offset-4 hover:text-muted-foreground transition-colors duration-150"
                      data-testid="event-organizer-link"
                    >
                      {event.organizer_name}
                    </Link>
                  </span>
                </p>
                {isExternal ? (
                  <p className="flex items-center gap-2 text-muted-foreground" data-testid="event-external-info-row">
                    <ExternalLink className="w-4 h-4 shrink-0" />
                    {externalIsFacebook ? t("Info og påmelding på Facebook") : t("Billetter/påmelding hos arrangøren")}
                  </p>
                ) : (
                  <p className="flex items-center gap-2 text-muted-foreground">
                    <Users className="w-4 h-4 shrink-0" />
                    {soldOut ? t("Utsolgt") : t("{n} av {cap} plasser ledige", { n: event.remaining, cap: event.capacity })}
                  </p>
                )}
              </div>
              {event.description && (
                <div className="pt-2">
                  <h2 className="text-lg font-semibold mb-2">{t("Om arrangementet")}</h2>
                  <p className="text-sm sm:text-base leading-relaxed text-foreground whitespace-pre-line" data-testid="event-description">
                    {event.description}
                  </p>
                </div>
              )}
              {hasLocation && (
                <div className="pt-2">
                  <div className="flex items-center justify-between mb-2">
                    <h2 className="text-lg font-semibold">{t("Slik finner du frem")}</h2>
                    <a
                      href={externalMapsUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 transition-colors duration-150"
                      data-testid="event-open-maps-link"
                    >
                      {t("Åpne i Google Maps")}
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>
                  <div className="rounded-2xl overflow-hidden border border-border bg-muted">
                    <iframe
                      title={t("Kart til arrangementet")}
                      src={`https://www.google.com/maps?q=${encodeURIComponent(mapQuery)}&z=14&hl=no&output=embed`}
                      className="w-full h-[300px] sm:h-[360px] border-0 block"
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      allowFullScreen
                      data-testid="event-map"
                    />
                  </div>
                </div>
              )}
              {/* Arrangørens org.nr og kontaktinfo – Vipps krever at dette er synlig der billettene selges */}
              <div className="pt-2 space-y-2">
                <OrganizerContactBlock name={event.organizer_name} orgNumber={event.organizer_org_number} contact={event.organizer_contact} compact />
                {/* «Butikken vår» – vises berre når arrangøren har open butikk med aktive varer */}
                <ShopTeaser organizerId={event.organizer_id} organizerName={event.organizer_name} />
                <BasarTeaser organizerId={event.organizer_id} organizerName={event.organizer_name} />
                {isExternal ? (
                  <p className="text-xs text-muted-foreground" data-testid="event-external-terms-note">
                    {t("Right in formidler ikke billetter til dette arrangementet. Pris, påmelding og vilkår finner du hos arrangøren.")}
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground" data-testid="event-terms-note">
                    {t("Kjøpet er bindende. Se")}{" "}
                    <Link to="/vilkar" className="underline underline-offset-4 hover:text-foreground transition-colors duration-150" data-testid="event-terms-link">{t("kjøpsvilkårene")}</Link>
                    {" "}{t("for refusjon, ombooking og kontakt.")}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Right: order card */}
          <div className="lg:col-span-5 lg:sticky lg:top-24">
            {isExternal ? (
            <Card className="border-border shadow-lg" data-testid="event-external-card">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between gap-3">
                  <CardTitle className="text-xl font-heading">{externalIsFacebook ? t("Facebook-arrangement") : t("Billetter hos arrangøren")}</CardTitle>
                  {event.external_price_text && <span className="font-heading font-semibold text-lg" data-testid="event-external-price-label">{event.external_price_text}</span>}
                </div>
                <p className="text-sm text-muted-foreground">
                  {externalIsFacebook
                    ? t("Arrangøren bruker Facebook til informasjon og påmelding. Du blir sendt til Facebook-arrangementet.")
                    : t("Billetter og påmelding håndteres av arrangøren på en ekstern side. Du blir sendt dit.")}
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                {event.is_over ? (
                  <div className="rounded-xl bg-secondary p-6 text-center" data-testid="event-over-order-closed">
                    <p className="font-medium">{t("Arrangementet er avsluttet")}</p>
                  </div>
                ) : (
                  <Button asChild className="w-full h-12 text-base" data-testid="event-external-cta">
                    <a href={event.external_url} target="_blank" rel="noopener noreferrer">
                      {externalIsFacebook ? <Facebook className="w-5 h-5 mr-2" /> : <ExternalLink className="w-5 h-5 mr-2" />}
                      {externalIsFacebook ? t("Gå til Facebook-arrangementet") : t("Gå til arrangementet")}
                    </a>
                  </Button>
                )}
                <p className="text-xs text-muted-foreground break-all" data-testid="event-external-host">
                  {t("Lenken åpnes i ny fane:")} {event.external_url?.replace(/^https?:\/\//, "")}
                </p>
              </CardContent>
            </Card>
            ) : (
            <>
            {/* Hjelpeknapp over kjøpskortet (brukarval): kort steg-for-steg for publikum på /hjelp */}
            {!event.is_over && (
              <Button asChild variant="secondary" size="sm" className="w-full mb-3 justify-between h-10" data-testid="event-help-button">
                <Link to="/hjelp">
                  <span className="inline-flex items-center gap-2">
                    <CircleHelp className="w-4 h-4" aria-hidden="true" />
                    {t("Første gang? Slik henter du billett")}
                  </span>
                  <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </Link>
              </Button>
            )}
            <Card className="border-border shadow-lg">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-xl font-heading">{isPaid ? t("Kjøp billetter") : t("Hent billetter")}</CardTitle>
                  <span className="font-heading font-semibold text-lg" data-testid="event-price-label">
                    {!isPaid
                      ? t("Gratis")
                      : event.min_price !== event.max_price
                        ? t("Fra {min} kr", { min: event.min_price })
                        : `${event.max_price} kr`}
                  </span>
                </div>
                {!event.is_over && (
                  <>
                    <p className="text-sm text-muted-foreground">
                      {soldOut ? t("Dette arrangementet er utsolgt.") : t("{n} billetter tilgjengelig", { n: event.remaining })}
                    </p>
                    <p className="text-xs text-muted-foreground" data-testid="event-free-seating-note">{t("Fri plassering – ingen nummererte seter.")}</p>
                  </>
                )}
              </CardHeader>
              <CardContent>
                {event.is_over ? (
                  <div className="rounded-xl bg-secondary p-6 text-center" data-testid="event-over-order-closed">
                    <p className="font-medium">{t("Arrangementet er avsluttet")}</p>
                    <p className="text-sm text-muted-foreground mt-1">{t("Det er ikke lenger mulig å hente billetter.")}</p>
                  </div>
                ) : soldOut ? (
                  wlJoined ? (
                    <div className="rounded-xl bg-accent text-accent-foreground p-6 text-center" data-testid="event-waitlist-success">
                      <p className="font-medium">{t("Du står på ventelisten!")}</p>
                      <p className="text-sm mt-1 opacity-80">{t("Vi sender deg en e-post hvis det blir ledige billetter. Førstemann til mølla.")}</p>
                    </div>
                  ) : (
                    <div className="space-y-4" data-testid="event-soldout-message">
                      <div className="rounded-xl bg-secondary p-4 text-center">
                        <p className="font-medium">{t("Utsolgt")}</p>
                        <p className="text-sm text-muted-foreground mt-1">{t("Sett deg på ventelisten, så gir vi beskjed hvis det blir ledig.")}</p>
                      </div>
                      <form onSubmit={joinWaitlist} className="space-y-3">
                        <div className="space-y-1.5">
                          <Label htmlFor="wl-name">{t("Fullt navn")}</Label>
                          <Input
                            id="wl-name"
                            value={wlName}
                            onChange={(e) => setWlName(e.target.value)}
                            placeholder="Ola Nordmann"
                            required
                            data-testid="event-waitlist-name-input"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="wl-email">{t("E-post")}</Label>
                          <Input
                            id="wl-email"
                            type="email"
                            value={wlEmail}
                            onChange={(e) => setWlEmail(e.target.value)}
                            placeholder="ola@example.com"
                            required
                            data-testid="event-waitlist-email-input"
                          />
                        </div>
                        <Button type="submit" className="w-full h-11" disabled={wlSubmitting} data-testid="event-waitlist-submit-button">
                          {wlSubmitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t("Melder på...")}</>) : t("Sett meg på venteliste")}
                        </Button>
                      </form>
                    </div>
                  )
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="order-email">{t("E-post")}</Label>
                      <Input
                        id="order-email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="ola@example.com"
                        required
                        data-testid="event-order-form-email-input"
                      />
                      <p className="text-xs text-muted-foreground">{t("Billettene vises med en gang, og lenken kan lagres eller sendes på e-post.")}</p>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="order-name">{t("Navn")} <span className="text-muted-foreground font-normal">{t("(valgfritt)")}</span></Label>
                      <Input
                        id="order-name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Ola Nordmann"
                        data-testid="event-order-form-name-input"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="order-phone">{t("Telefon")} <span className="text-muted-foreground font-normal">{t("(valgfritt)")}</span></Label>
                      <Input
                        id="order-phone"
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="912 34 567"
                        maxLength={30}
                        data-testid="event-order-form-phone-input"
                      />
                      <p className="text-xs text-muted-foreground">{t("Norsk nummer, 8 siffer.")}</p>
                    </div>
                    {hasTypes ? (
                      <div className="space-y-2">
                        <Label>{t("Billetter (maks {n} totalt)", { n: maxQty })}</Label>
                        <div className="space-y-2" data-testid="event-order-type-list">
                          {event.ticket_types.map((tt) => (
                            <div key={tt} className="flex items-center justify-between rounded-xl border border-border px-3 py-2.5">
                              <div>
                                <span className="text-sm font-medium">{tt}</span>
                                <span className="block text-xs text-muted-foreground" data-testid={`event-order-type-price-${tt}`}>
                                  {unitPrice(tt) > 0 ? `${unitPrice(tt)} kr` : t("Gratis")}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button
                                  type="button"
                                  variant="secondary"
                                  size="icon"
                                  className="h-9 w-9"
                                  onClick={() => adjustType(tt, -1)}
                                  disabled={(typeQty[tt] || 0) <= 0}
                                  data-testid={`event-order-type-decrease-${tt}`}
                                >
                                  <Minus className="w-4 h-4" />
                                </Button>
                                <span className="w-8 text-center font-medium tabular-nums" data-testid={`event-order-type-qty-${tt}`}>{typeQty[tt] || 0}</span>
                                <Button
                                  type="button"
                                  variant="secondary"
                                  size="icon"
                                  className="h-9 w-9"
                                  onClick={() => adjustType(tt, 1)}
                                  disabled={totalQty >= maxQty}
                                  data-testid={`event-order-type-increase-${tt}`}
                                >
                                  <Plus className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                        <p className="text-xs text-muted-foreground">{t("Totalt:")} {totalQty} {totalQty === 1 ? t("billett") : t("billetter")}</p>
                      </div>
                    ) : (
                      <div className="space-y-1.5">
                        <Label>{t("Antall billetter")}</Label>
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            variant="secondary"
                            size="icon"
                            className="h-11 w-11"
                            onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                            disabled={quantity <= 1}
                            data-testid="event-order-form-quantity-decrease"
                          >
                            <Minus className="w-4 h-4" />
                          </Button>
                          <Input
                            type="number"
                            min={1}
                            max={maxQty}
                            value={quantity}
                            onChange={(e) => {
                              const v = parseInt(e.target.value || "1", 10);
                              setQuantity(Math.min(maxQty, Math.max(1, isNaN(v) ? 1 : v)));
                            }}
                            className="h-11 text-center font-medium w-20"
                            data-testid="event-order-form-quantity-input"
                          />
                          <Button
                            type="button"
                            variant="secondary"
                            size="icon"
                            className="h-11 w-11"
                            onClick={() => setQuantity((q) => Math.min(maxQty, q + 1))}
                            disabled={quantity >= maxQty}
                            data-testid="event-order-form-quantity-increase"
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                          <span className="text-xs text-muted-foreground ml-1">{t("maks")} {maxQty}</span>
                        </div>
                      </div>
                    )}
                    {isPaid && (
                      discount ? (
                        <div className="flex items-center justify-between rounded-xl border border-border bg-accent/60 px-3 py-2.5" data-testid="discount-applied-row">
                          <span className="text-sm flex items-center gap-1.5">
                            <BadgePercent className="w-4 h-4" />
                            <span className="font-mono font-semibold">{discount.code}</span> – {discount.description}
                          </span>
                          <button type="button" onClick={removeDiscount} className="rounded-full hover:bg-secondary p-1" data-testid="discount-remove-button" aria-label={t("Fjern rabattkode")}>
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <Input
                            value={discountCodeInput}
                            onChange={(e) => setDiscountCodeInput(e.target.value.toUpperCase().replace(/\s+/g, ""))}
                            placeholder={t("Rabattkode (valgfritt)")}
                            className="font-mono uppercase"
                            maxLength={40}
                            data-testid="discount-code-order-input"
                          />
                          <Button
                            type="button"
                            variant="secondary"
                            onClick={applyDiscount}
                            disabled={checkingDiscount || !discountCodeInput.trim()}
                            data-testid="discount-apply-button"
                          >
                            {checkingDiscount ? <Loader2 className="w-4 h-4 animate-spin" /> : t("Bruk")}
                          </Button>
                        </div>
                      )
                    )}
                    {totalAmount > 0 && (
                      <div className="rounded-xl bg-secondary px-4 py-3 space-y-1.5" data-testid="event-order-total">
                        {discountAmount > 0 && (
                          <>
                            <div className="flex items-center justify-between text-sm text-muted-foreground">
                              <span>{t("Sum")}</span>
                              <span className="tabular-nums">{totalAmount} kr</span>
                            </div>
                            <div className="flex items-center justify-between text-sm text-[hsl(var(--success))]" data-testid="event-order-discount-line">
                              <span>{t("Rabatt")} ({discount.code})</span>
                              <span className="tabular-nums">−{discountAmount} kr</span>
                            </div>
                          </>
                        )}
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">{t("Totalt å betale")}</span>
                          <span className="font-heading font-semibold tabular-nums" data-testid="event-order-payable">{payableAmount} kr</span>
                        </div>
                      </div>
                    )}
                    {doorAvailable && (
                      <div className="space-y-2" data-testid="event-payment-method">
                        <p className="text-sm font-medium">{t("Betaling")}</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <button
                            type="button"
                            onClick={() => setPaymentMethod("vipps")}
                            aria-pressed={!payAtDoor}
                            className={`flex items-start gap-3 rounded-xl border p-3 text-left transition-colors duration-150 ${!payAtDoor ? "border-brand bg-brand-soft/40" : "border-border hover:bg-secondary/60"}`}
                            data-testid="event-payment-vipps"
                          >
                            <Smartphone className="w-4 h-4 mt-0.5 shrink-0" />
                            <span>
                              <span className="block text-sm font-medium">{t("Betal med Vipps")}</span>
                              <span className="block text-xs text-muted-foreground">
                                {vippsAvailable ? t("Billettene er betalt og klare med en gang.") : t("Kommer snart – arrangøren gjør klar Vipps.")}
                              </span>
                            </span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setPaymentMethod("door")}
                            aria-pressed={payAtDoor}
                            className={`flex items-start gap-3 rounded-xl border p-3 text-left transition-colors duration-150 ${payAtDoor ? "border-brand bg-brand-soft/40" : "border-border hover:bg-secondary/60"}`}
                            data-testid="event-payment-door"
                          >
                            <HandCoins className="w-4 h-4 mt-0.5 shrink-0" />
                            <span>
                              <span className="block text-sm font-medium">{t("Reserver billetter")}</span>
                              <span className="block text-xs text-muted-foreground">{t("Du får QR-billett nå, merket «ikke betalt», og betaler {n} kr til arrangøren ved inngangen.", { n: payableAmount })}</span>
                            </span>
                          </button>
                        </div>
                        {vippsPending && (
                          <div className="rounded-xl border border-[hsl(var(--warning))]/50 bg-[hsl(var(--warning))]/15 px-4 py-3 text-sm" data-testid="event-vipps-pending-notice">
                            {t("Arrangøren gjør klar betaling med Vipps. Ta en kikk igjen i morgen.")}
                          </div>
                        )}
                      </div>
                    )}
                    {paymentsBlocked && (
                      <div className="rounded-xl border border-[hsl(var(--warning))]/50 bg-[hsl(var(--warning))]/15 px-4 py-3 text-sm" data-testid="event-payments-disabled-warning">
                        <span className="font-medium">{t("Billettsalget åpner snart.")}</span> {t("Arrangøren gjør klart betaling med Vipps – ta en titt igjen om litt.")}
                      </div>
                    )}
                    <Button
                      type="submit"
                      className="w-full h-11"
                      disabled={submitting || totalQty < 1 || paymentsBlocked || vippsPending}
                      data-testid="event-order-form-submit-button"
                    >
                      {submitting ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{payableAmount > 0 && !payAtDoor ? t("Starter betaling...") : t("Henter billetter...")}</>
                      ) : paymentsBlocked ? (
                        t("Billettsalget åpner snart")
                      ) : vippsPending ? (
                        t("Vipps-betaling kommer snart")
                      ) : payAtDoor ? (
                        t("Reserver billetter – betal {n} kr ved inngangen", { n: payableAmount })
                      ) : payableAmount > 0 ? (
                        t("Betal {n} kr med Vipps", { n: payableAmount })
                      ) : (
                        t("Hent billetter")
                      )}
                    </Button>
                  </form>
                )}
                <p className="mt-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-xs text-muted-foreground" data-testid="event-sales-terms-row">
                  <Link to="/hjelp" className="inline-flex items-center gap-1 underline underline-offset-4 hover:text-foreground transition-colors duration-150" data-testid="event-help-link">
                    <CircleHelp className="w-3.5 h-3.5" aria-hidden="true" />
                    {t("Slik henter du billett")}
                  </Link>
                  <Link to="/vilkar" className="underline underline-offset-4 hover:text-foreground transition-colors duration-150" data-testid="event-sales-terms-link">
                    {t("Vilkår for salg")}
                  </Link>
                </p>
              </CardContent>
            </Card>
            </>
            )}
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
